/* eslint-disable max-lines */
import * as React from "react";
import { connect } from "react-redux";
import { values } from "lodash";
import * as moment from "moment";
import { Table } from "semantic-ui-react";

import { OldProgressBarQuestionnaireSectionUnconnected } from "@teamprevent/common/questionnaires/components/OldProgressBarQuestionnaireSection";
import { getQuestionConfig, CustomQuestionConfig } from "@teamprevent/common/questionnaires/questionnaireConfig";
import { mapDispatchToQuestionnaireSectionProps, makeMapStateToQuestionnaireSectionProps } from "@teamprevent/common/questionnaires/mappers";
import { CustomQuestionsQuestionnaireSectionProps } from "@teamprevent/common/questionnaires/QuestionnaireSectionProps";
import { MultiButtonQuestion } from "@teamprevent/common/questionnaires/components/questions/MultiButtonQuestion";
import { TextQuestion } from "@teamprevent/common/questionnaires/components/questions/TextQuestion";
import { YesNoWithFollowUpQuestion } from "@teamprevent/common/questionnaires/components/questions/YesNoWithFollowUpQuestion";
import { getAnswerToQuestionWithId } from "@teamprevent/common/questionnaires/getAnswerToQuestionWithId";
import { CheckboxQuestion } from "@teamprevent/common/questionnaires/components/questions/CheckboxQuestion";
import { OldDateQuestion } from "@teamprevent/common/questionnaires/components/questions/OldDateQuestion";
import { DropdownQuestion } from "@teamprevent/common/questionnaires/components/questions/DropdownQuestion";
import { NumberQuestion } from "@teamprevent/common/questionnaires/components/questions/NumberQuestion";
import { YesNoNaDropdownQuestion } from "@teamprevent/common/questionnaires/components/questions/YesNoNaDropdownQuestion";
import { usePrevious } from "@teamprevent/common/customHooks";

import { ApplicationState } from "../../applicationState";
import * as questionIds from "../consultationQuestions/questionIds";
import { ReasonsForAttending } from "../consultationQuestions/audiometryLookupTableValue";
import { audiometryHearingCategoryOptions } from "../models";
import { sections } from "../consultation";

const reviewDateOtherAnswer = "Other";

interface AdditionalProps {
    employeeDob: moment.Moment;
}

type AllProps = CustomQuestionsQuestionnaireSectionProps & AdditionalProps;

const HealthSurveillanceConsultationAudiometrySectionUnconnected: React.FunctionComponent<AllProps> = (props: AllProps) => {

    const questions = values(props.questions || {});
    const previousQuestions = usePrevious(questions);

    const onLeftFrequencyChange = (questionId: string) =>
        (answer: number) => {
            const newFrequency =
                (questionId === questionIds.AudiometryLeftEar1_0 ?
                    answer : (getAnswerToQuestionWithId(questions, questionIds.AudiometryLeftEar1_0) as number)) +
                (questionId === questionIds.AudiometryLeftEar2_0 ?
                    answer : (getAnswerToQuestionWithId(questions, questionIds.AudiometryLeftEar2_0) as number)) +
                (questionId === questionIds.AudiometryLeftEar3_0 ?
                    answer : (getAnswerToQuestionWithId(questions, questionIds.AudiometryLeftEar3_0) as number)) +
                (questionId === questionIds.AudiometryLeftEar4_0 ?
                    answer : (getAnswerToQuestionWithId(questions, questionIds.AudiometryLeftEar4_0) as number)) +
                (questionId === questionIds.AudiometryLeftEar6_0 ?
                    answer : (getAnswerToQuestionWithId(questions, questionIds.AudiometryLeftEar6_0) as number));
            const newAnswer = typeof newFrequency === "number" ? newFrequency : null;
            props.updateAnswer({
                sectionId: sections.Audiometry.id, id: questionIds.AudiometryLeftEarFrequency, questionnaireKey: props.questionnaireKey, question: {
                    question: (getQuestionConfig(props.questionConfigs, questionIds.AudiometryLeftEarFrequency) as CustomQuestionConfig).questionText,
                    isValid: true,
                    answer: newAnswer
                }
            });
        };

    const onRightFrequencyChange = (questionId: string) =>
        (answer: number) => {
            const newFrequency =
                (questionId === questionIds.AudiometryRightEar1_0 ?
                    answer : (getAnswerToQuestionWithId(questions, questionIds.AudiometryRightEar1_0) as number)) +
                (questionId === questionIds.AudiometryRightEar2_0 ?
                    answer : (getAnswerToQuestionWithId(questions, questionIds.AudiometryRightEar2_0) as number)) +
                (questionId === questionIds.AudiometryRightEar3_0 ?
                    answer : (getAnswerToQuestionWithId(questions, questionIds.AudiometryRightEar3_0) as number)) +
                (questionId === questionIds.AudiometryRightEar4_0 ?
                    answer : (getAnswerToQuestionWithId(questions, questionIds.AudiometryRightEar4_0) as number)) +
                (questionId === questionIds.AudiometryRightEar6_0 ?
                    answer : (getAnswerToQuestionWithId(questions, questionIds.AudiometryRightEar6_0) as number));
            const newAnswer = typeof newFrequency === "number" ? newFrequency : null;
            props.updateAnswer({
                sectionId: sections.Audiometry.id, id: questionIds.AudiometryRightEarFrequency, questionnaireKey: props.questionnaireKey, question: {
                    question: (getQuestionConfig(props.questionConfigs, questionIds.AudiometryRightEarFrequency) as CustomQuestionConfig).questionText,
                    isValid: true,
                    answer: newAnswer
                }
            });
        };

    const hasAnswerChanged = React.useCallback((questionId: string) => {
        const currentAnswer = getAnswerToQuestionWithId(questions, questionId);
        const previousAnswer = getAnswerToQuestionWithId(previousQuestions, questionId);
        return !!currentAnswer && (currentAnswer !== previousAnswer); // "!!currentAnswer" to prevent infinite loop
    }, [previousQuestions, questions]);

    const { readonly, questionnaireKey, questionConfigs, updateAnswer } = props;

    React.useEffect(() => {
        if (!readonly) {
            const hasAudiometryLeftEarFrequencyAnswerChanged = hasAnswerChanged(questionIds.AudiometryLeftEarFrequency);
            const hasAudiometryRightEarFrequencyAnswerChanged = hasAnswerChanged(questionIds.AudiometryRightEarFrequency);

            if (hasAudiometryLeftEarFrequencyAnswerChanged || hasAudiometryRightEarFrequencyAnswerChanged) {
                const leftFrequency = getAnswerToQuestionWithId(questions, questionIds.AudiometryLeftEarFrequency);
                const rightFrequency = getAnswerToQuestionWithId(questions, questionIds.AudiometryRightEarFrequency);
                let difference: number | null;
                if (typeof leftFrequency !== "number" || typeof rightFrequency !== "number") {
                    difference = null;
                } else {
                    // 6 kHz answer should not be taken into account for the "Audiometry Unilateral Difference"
                    const audiometryLeftEar6_0Answer = (getAnswerToQuestionWithId(questions, questionIds.AudiometryLeftEar6_0) as number) ?? 0;
                    const audiometryRightEar6_0Answer = (getAnswerToQuestionWithId(questions, questionIds.AudiometryRightEar6_0) as number) ?? 0;
                    difference = (leftFrequency - audiometryLeftEar6_0Answer) - (rightFrequency - audiometryRightEar6_0Answer);
                }
                updateAnswer({
                    sectionId: sections.Audiometry.id, id: questionIds.AudiometryUnilateralDifference, questionnaireKey, question: {
                        question: (
                            getQuestionConfig(questionConfigs, questionIds.AudiometryUnilateralDifference) as CustomQuestionConfig).questionText,
                        isValid: true,
                        answer: difference && Math.abs(difference)
                    }
                });
                let unilateralAction: string | null;
                if (difference === null) {
                    unilateralAction = null;
                } else if (difference > 40) {
                    unilateralAction = "Left unilateral hearing loss indicated - Must set a repeat audio or refer to GP/OHP at end of consultation";
                } else if (difference < -40) {
                    unilateralAction = "Right unilateral hearing loss indicated - Must set a repeat audio or refer to GP/OHP at end of consultation";
                } else {
                    unilateralAction = "No action required";
                }
                updateAnswer({
                    sectionId: sections.Audiometry.id, id: questionIds.AudiometryUnilateralAction, questionnaireKey, question: {
                        question: (getQuestionConfig(questionConfigs, questionIds.AudiometryUnilateralAction) as CustomQuestionConfig).questionText,
                        isValid: true,
                        answer: unilateralAction
                    }
                });
            }
        }
    }, [hasAnswerChanged, questions, readonly, questionnaireKey, questionConfigs, updateAnswer]);

    return (
        <OldProgressBarQuestionnaireSectionUnconnected
            {...props}
            {...sections.Audiometry}
        >
            {YesNoWithFollowUpQuestion({
                readonly: props.readonly,
                questionConfigs: [
                    getQuestionConfig(props.questionConfigs, questionIds.AudiometryUnableToCarryOut),
                    getQuestionConfig(props.questionConfigs, questionIds.AudiometryUnableToCarryOutReason)
                ],
                mandatory: true,
                followUpMandatory: true,
                questions: props.questions
            })}
            {getAnswerToQuestionWithId(questions, questionIds.AudiometryUnableToCarryOut) !== MultiButtonQuestion.YesOption && <>
                <TextQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.AudiometryMachineSerialNumber)}
                    mandatory
                />
                <OldDateQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.AudiometryCalibrationDate)}
                    mandatory
                />
                <DropdownQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.AudiometryReasonForAttending)}
                    options={ReasonsForAttending.map(g => ({ text: g, value: g }))}
                    mandatory
                />
                <OldDateQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.AudiometryDateOfLastTest)}
                    mandatory
                />
                <DropdownQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.AudiometryGender)}
                    options={[
                        { text: "Male", value: "Male" },
                        { text: "Female", value: "Female" }
                    ]}
                    mandatory
                />
                <NumberQuestion
                    readonly
                    answer={props.employeeDob && moment().diff(props.employeeDob, "years")}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.AudiometryAge)}
                />
                <DropdownQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.AudiometryLeftEarWaxVisible)}
                    options={[
                        { text: "Tympanic membrane fully visible", value: "Tympanic membrane fully visible" },
                        { text: "Partially visible", value: "Partially visible" },
                        { text: "Completely occluded by wax", value: "Completely occluded by wax" }
                    ]}
                />
                <DropdownQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.AudiometryRightEarWaxVisible)}
                    options={[
                        { text: "Tympanic membrane fully visible", value: "Tympanic membrane fully visible" },
                        { text: "Partially visible", value: "Partially visible" },
                        { text: "Completely occluded by wax", value: "Completely occluded by wax" }
                    ]}
                />
                {YesNoWithFollowUpQuestion({
                    questionConfigs: [
                        getQuestionConfig(props.questionConfigs, questionIds.AudiometryScarringLeftEar),
                        getQuestionConfig(props.questionConfigs, questionIds.AudiometryScarringLeftEarComments)
                    ],
                    readonly: props.readonly,
                    questions: props.questions
                })}
                {YesNoWithFollowUpQuestion({
                    questionConfigs: [
                        getQuestionConfig(props.questionConfigs, questionIds.AudiometryScarringRightEar),
                        getQuestionConfig(props.questionConfigs, questionIds.AudiometryScarringRightEarComments)
                    ],
                    readonly: props.readonly,
                    questions: props.questions
                })}
                {YesNoWithFollowUpQuestion({
                    questionConfigs: [
                        getQuestionConfig(props.questionConfigs, questionIds.AudiometryInfectionCongestionLeftEar),
                        getQuestionConfig(props.questionConfigs, questionIds.AudiometryInfectionCongestionLeftEarComments)
                    ],
                    readonly: props.readonly,
                    questions: props.questions
                })}
                {YesNoWithFollowUpQuestion({
                    questionConfigs: [
                        getQuestionConfig(props.questionConfigs, questionIds.AudiometryInfectionCongestionRightEar),
                        getQuestionConfig(props.questionConfigs, questionIds.AudiometryInfectionCongestionRightEarComments)
                    ],
                    readonly: props.readonly,
                    questions: props.questions
                })}
                <TextQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.AudiometryEntIssues)}
                    mandatory
                />

                <Table>
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell content="Frequency kHz" />
                            <Table.HeaderCell content=".500" />
                            <Table.HeaderCell content=".750" />
                            <Table.HeaderCell content="1.0" />
                            <Table.HeaderCell content="1.5" />
                            <Table.HeaderCell content="2.0" />
                            <Table.HeaderCell content="3.0" />
                            <Table.HeaderCell content="4.0" />
                            <Table.HeaderCell content="6.0" />
                            <Table.HeaderCell content="8.0" />
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        <Table.Row>
                            <Table.Cell content="Left Ear dB" />
                            <Table.Cell>
                                <NumberQuestion
                                    readonly={props.readonly}
                                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.AudiometryLeftEar0_500)}
                                    min={-20}
                                    max={110}
                                    noLabel
                                    hideDivider
                                />
                            </Table.Cell>
                            <Table.Cell>
                                <NumberQuestion
                                    readonly={props.readonly}
                                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.AudiometryLeftEar0_750)}
                                    min={-20}
                                    max={110}
                                    noLabel
                                    hideDivider
                                />
                            </Table.Cell>
                            <Table.Cell>
                                <NumberQuestion
                                    readonly={props.readonly}
                                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.AudiometryLeftEar1_0)}
                                    min={-20}
                                    max={110}
                                    noLabel
                                    hideDivider
                                    onAnswerChange={onLeftFrequencyChange(questionIds.AudiometryLeftEar1_0)}
                                />
                            </Table.Cell>
                            <Table.Cell>
                                <NumberQuestion
                                    readonly={props.readonly}
                                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.AudiometryLeftEar1_5)}
                                    min={-20}
                                    max={110}
                                    noLabel
                                    hideDivider
                                />
                            </Table.Cell>
                            <Table.Cell>
                                <NumberQuestion
                                    readonly={props.readonly}
                                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.AudiometryLeftEar2_0)}
                                    min={-20}
                                    max={110}
                                    noLabel
                                    hideDivider
                                    onAnswerChange={onLeftFrequencyChange(questionIds.AudiometryLeftEar2_0)}
                                />
                            </Table.Cell>
                            <Table.Cell>
                                <NumberQuestion
                                    readonly={props.readonly}
                                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.AudiometryLeftEar3_0)}
                                    min={-20}
                                    max={110}
                                    noLabel
                                    hideDivider
                                    onAnswerChange={onLeftFrequencyChange(questionIds.AudiometryLeftEar3_0)}
                                />
                            </Table.Cell>
                            <Table.Cell>
                                <NumberQuestion
                                    readonly={props.readonly}
                                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.AudiometryLeftEar4_0)}
                                    min={-20}
                                    max={110}
                                    noLabel
                                    hideDivider
                                    onAnswerChange={onLeftFrequencyChange(questionIds.AudiometryLeftEar4_0)}
                                />
                            </Table.Cell>
                            <Table.Cell>
                                <NumberQuestion
                                    readonly={props.readonly}
                                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.AudiometryLeftEar6_0)}
                                    min={-20}
                                    max={110}
                                    noLabel
                                    hideDivider
                                    onAnswerChange={onLeftFrequencyChange(questionIds.AudiometryLeftEar6_0)}
                                />
                            </Table.Cell>
                            <Table.Cell>
                                <NumberQuestion
                                    readonly={props.readonly}
                                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.AudiometryLeftEar8_0)}
                                    min={-20}
                                    max={110}
                                    noLabel
                                    hideDivider
                                />
                            </Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell content="Right Ear dB" />
                            <Table.Cell>
                                <NumberQuestion
                                    readonly={props.readonly}
                                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.AudiometryRightEar0_500)}
                                    min={-20}
                                    max={110}
                                    noLabel
                                    hideDivider
                                />
                            </Table.Cell>
                            <Table.Cell>
                                <NumberQuestion
                                    readonly={props.readonly}
                                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.AudiometryRightEar0_750)}
                                    min={-20}
                                    max={110}
                                    noLabel
                                    hideDivider
                                />
                            </Table.Cell>
                            <Table.Cell>
                                <NumberQuestion
                                    readonly={props.readonly}
                                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.AudiometryRightEar1_0)}
                                    min={-20}
                                    max={110}
                                    noLabel
                                    hideDivider
                                    onAnswerChange={onRightFrequencyChange(questionIds.AudiometryRightEar1_0)}
                                />
                            </Table.Cell>
                            <Table.Cell>
                                <NumberQuestion
                                    readonly={props.readonly}
                                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.AudiometryRightEar1_5)}
                                    min={-20}
                                    max={110}
                                    noLabel
                                    hideDivider
                                />
                            </Table.Cell>
                            <Table.Cell>
                                <NumberQuestion
                                    readonly={props.readonly}
                                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.AudiometryRightEar2_0)}
                                    min={-20}
                                    max={110}
                                    noLabel
                                    hideDivider
                                    onAnswerChange={onRightFrequencyChange(questionIds.AudiometryRightEar2_0)}
                                />
                            </Table.Cell>
                            <Table.Cell>
                                <NumberQuestion
                                    readonly={props.readonly}
                                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.AudiometryRightEar3_0)}
                                    min={-20}
                                    max={110}
                                    noLabel
                                    hideDivider
                                    onAnswerChange={onRightFrequencyChange(questionIds.AudiometryRightEar3_0)}
                                />
                            </Table.Cell>
                            <Table.Cell>
                                <NumberQuestion
                                    readonly={props.readonly}
                                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.AudiometryRightEar4_0)}
                                    min={-20}
                                    max={110}
                                    noLabel
                                    hideDivider
                                    onAnswerChange={onRightFrequencyChange(questionIds.AudiometryRightEar4_0)}
                                />
                            </Table.Cell>
                            <Table.Cell>
                                <NumberQuestion
                                    readonly={props.readonly}
                                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.AudiometryRightEar6_0)}
                                    min={-20}
                                    max={110}
                                    noLabel
                                    hideDivider
                                    onAnswerChange={onRightFrequencyChange(questionIds.AudiometryRightEar6_0)}
                                />
                            </Table.Cell>
                            <Table.Cell>
                                <NumberQuestion
                                    readonly={props.readonly}
                                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.AudiometryRightEar8_0)}
                                    min={-20}
                                    max={110}
                                    noLabel
                                    hideDivider
                                />
                            </Table.Cell>
                        </Table.Row>
                    </Table.Body>
                </Table>
                <NumberQuestion
                    readonly
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.AudiometryLeftEarFrequency)}
                />
                <NumberQuestion
                    readonly
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.AudiometryRightEarFrequency)}
                />
                <TextQuestion
                    readonly
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.AudiometryUnilateralDifference)}
                />
                <TextQuestion
                    readonly
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.AudiometryUnilateralAction)}
                />
                <DropdownQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.AudiometryHearingCategory)}
                    options={audiometryHearingCategoryOptions}
                    mandatory
                />
                <CheckboxQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.AudiometryResultsExplained)}
                    value="Yes"
                />
                <MultiButtonQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.AudiometryPpeAvailableForInspection)}
                    mandatory
                />
                {getAnswerToQuestionWithId(questions, questionIds.AudiometryPpeAvailableForInspection) === MultiButtonQuestion.YesOption &&
                    <>
                        <MultiButtonQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.AudiometryPpeComfortableAndFitForPurpose)}
                            hideDivider
                            mandatory
                        />
                        <TextQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.AudiometryPpeComfortableAndFitForPurposeComments)}
                        />
                    </>
                }
                <MultiButtonQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.AudiometryHearingProtectionLeafletOrAdviceGiven)}
                    mandatory
                />
                <DropdownQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.AudiometryReferralRequired)}
                    options={[
                        { text: "No", value: "No" },
                        { text: "Referred to GP", value: "Referred to GP" },
                        { text: "Referral to company OHA/OHP", value: "Referral to company OHA/OHP" },
                        { text: "GP and company referral", value: "GP and company referral" }
                    ]}
                    mandatory
                />
                <YesNoNaDropdownQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.AudiometryAdviceRecommendationLetterForGpSupplied)}
                    mandatory
                />
                <DropdownQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.AudiometryReviewDateRecommended)}
                    options={[
                        { text: "Annual", value: "Annual" },
                        { text: "3 years", value: "3 years" },
                        { text: reviewDateOtherAnswer, value: reviewDateOtherAnswer }
                    ]}
                    mandatory
                />
                {getAnswerToQuestionWithId(questions, questionIds.AudiometryReviewDateRecommended) === reviewDateOtherAnswer &&
                    <TextQuestion
                        readonly={props.readonly}
                        questionConfig={getQuestionConfig(props.questionConfigs, questionIds.AudiometryReviewDateRecommendedComments)}
                        mandatory
                    />
                }
            </>}
        </OldProgressBarQuestionnaireSectionUnconnected>
    );
};

const mapStateToProps = (state: ApplicationState, ownProps: any) => {
    return {
        ...makeMapStateToQuestionnaireSectionProps(sections.Audiometry.id)(state, ownProps)
    };
};

export const HealthSurveillanceConsultationAudiometrySection = connect(
    mapStateToProps,
    mapDispatchToQuestionnaireSectionProps
)(HealthSurveillanceConsultationAudiometrySectionUnconnected);
